/**
 * CTASection Component
 * Design: "Light with Dark Accents"
 * Light background, dark contact card as accent, blue CTA
 * No scroll animations – content always fully visible
 */
import { ArrowRight, Calendar, Mail, MapPin, Phone } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

export default function CTASection() {
  const { t } = useLanguage();

  return (
    <section id="contact" className="relative py-24 lg:py-32 overflow-hidden" style={{ background: "#F8F9FC" }}>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: CTA Text */}
          <div>
            <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-sm text-blue-600 font-sora font-medium">{t.cta.badge}</span>
            </div>

            <h2 className="font-sora font-black text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight mb-6">
              {t.cta.headline1}<br />
              <span className="text-blue-600">{t.cta.headline2}</span>
            </h2>

            <p className="text-slate-800 text-lg leading-relaxed mb-8 font-inter">
              {t.cta.subtext}
            </p>

            <div className="space-y-4 mb-8">
              {t.cta.points.map((point, i) => (
                <div key={i} className="flex items-center gap-3">
                  {i === 0 ? <Calendar size={16} className="text-blue-600 flex-shrink-0" /> : <ArrowRight size={16} className="text-blue-600 flex-shrink-0" />}
                  <span className="text-slate-800 font-inter">{point}</span>
                </div>
              ))}
            </div>

            <a
              href={t.calendlyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-2 px-8 py-4 rounded-xl text-base font-sora font-semibold group"
            >
              {t.cta.ctaButton}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
          </div>

          {/* Right: Contact Card */}
          <div>
            <div className="rounded-2xl p-8 border border-slate-200 bg-white shadow-lg">
              <h3 className="font-sora font-bold text-xl text-slate-900 mb-6">{t.cta.contactTitle}</h3>

              <div className="space-y-4">
                <a
                  href="mailto:info@cem-yigit.de"
                  className="flex items-center gap-4 p-4 rounded-xl bg-[#F8F9FC] border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-all group"
                >
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Mail size={18} className="text-blue-600" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-600 font-sora uppercase tracking-wider mb-0.5">{t.cta.emailLabel}</div>
                    <div className="text-slate-800 font-sora font-medium group-hover:text-blue-600 transition-colors">info@cem-yigit.de</div>
                  </div>
                </a>

                <div className="flex items-center gap-4 p-4 rounded-xl bg-[#F8F9FC] border border-slate-200">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <MapPin size={18} className="text-emerald-600" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-600 font-sora uppercase tracking-wider mb-0.5">{t.cta.locationLabel}</div>
                    <div className="text-slate-800 font-sora font-medium">{t.cta.location1}</div>
                    <div className="text-slate-700 text-sm font-inter">{t.cta.location2}</div>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 rounded-xl bg-[#F8F9FC] border border-slate-200">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Phone size={18} className="text-blue-600" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-600 font-sora uppercase tracking-wider mb-0.5">{t.cta.companyLabel}</div>
                    <div className="text-slate-800 font-sora font-medium">{t.cta.companyName}</div>
                    <div className="text-slate-700 text-sm font-inter">{t.cta.companyDesc}</div>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-100">
                <p className="text-slate-700 text-sm font-inter leading-relaxed">
                  {t.cta.footerNote}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
